import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useState,
} from "react";
import * as Scrivito from "scrivito";
import { useItemsPerPage, useUserDepartments } from "hooks";
import { getHomepage } from "utils";
import { PROJECTS_PER_PAGE } from "variables";
import { useProjectListQuery } from "../hooks/useProjectListQuery";

type PageParams = {
  page?: string;
  projectId?: string;
};

type DepartmentOption = {
  value?: string;
  label?: string | null;
};

type ProjectListProviderProps = {
  children: ReactNode;
  page: Scrivito.Obj;
};

type ProjectListQueryParams = {
  limit: number;
  page: number;
  order: string;
  filters: {
    searchQuery: string | null;
    statuses: string[];
    sharedStatusSwitcher: string | null;
    departmentId: string | undefined;
  };
};

export type ProjectListContextValue = {
  projectsToDelete: string[];
  setProjectsToDelete: Dispatch<SetStateAction<string[]>>;
  projects: unknown[];
  refetchProjectList: (opts?: Record<string, unknown>) => void;
  currentPageNo: number;
  setCurrentPageNo: Dispatch<SetStateAction<number>>;
  resetUrlParams: () => void;
  projectId?: string;
  projectsCount: number;
  itemsPerPage: number;
  resetCallback: () => void;
  searchQuery: string | null;
  setSearchQuery: Dispatch<SetStateAction<string | null>>;
  overallStatistics: unknown;
  setSharedStatusSwitcher: Dispatch<SetStateAction<string | null>>;
  projectStatuses: unknown[];
  setFilterStatuses: Dispatch<SetStateAction<string[]>>;
  setFilters: Dispatch<SetStateAction<unknown[]>>;
  filters: unknown[];
  statistics: unknown;
  handleItemsPerPageChange: (value: number) => void;
  error: Error | null;
  isLoading: boolean;
  isOrderDescending: boolean;
  projectItemObj: unknown;
  projectConfiguratorObj: unknown;
  setIsOrderDescending: Dispatch<SetStateAction<boolean>>;
  statusColors: Record<string, string> | null;
  filteredCount: number;
  pagesCount: number;
  setIsItemsPerPageChanged: Dispatch<SetStateAction<boolean>>;
  selectedDepartment: DepartmentOption | null;
  setSelectedDepartment: (option: DepartmentOption | null) => void;
  departmentOptions: DepartmentOption[];
  handleSetCurrentPage: (pageToSet?: number) => void;
  isSelectAll: boolean;
  setIsSelectAll: Dispatch<SetStateAction<boolean>>;
  projectListQueryParams: ProjectListQueryParams;
};

export const ProjectListContext = createContext<ProjectListContextValue | null>(
  null,
);

export const ProjectListProvider = Scrivito.connect(
  ({ children, page }: ProjectListProviderProps) => {
    const {
      itemsPerPage,
      handleItemsPerPageChange,
      isItemsPerPageChanged,
      setIsItemsPerPageChanged,
    } = useItemsPerPage(PROJECTS_PER_PAGE);

    const pageParams = Scrivito.currentPageParams() as PageParams;

    const [currentPageNo, setCurrentPageNo] = useState(() =>
      isItemsPerPageChanged ? 1 : Number(pageParams.page) || 1,
    );
    const home = getHomepage();
    const { projectId } = pageParams;

    const handleSetCurrentPage = useCallback(
      (pageToSet: number = 1) => {
        setIsItemsPerPageChanged(false);
        setCurrentPageNo(pageToSet);
        setProjectsToDelete([]);
        setIsSelectAll(false);

        Scrivito.navigateTo(() => Scrivito.currentPage(), {
          params: {
            page: String(pageToSet),
            ...(projectId && {
              projectId,
            }),
          },
        });
      },
      [setIsItemsPerPageChanged, projectId],
    );

    useEffect(() => {
      if (isItemsPerPageChanged) {
        setCurrentPageNo(1);
        Scrivito.navigateTo(() => Scrivito.currentPage(), {
          params: { page: "1" },
        });
      }
    }, [isItemsPerPageChanged]);

    const [isOrderDescending, setIsOrderDescending] = useState(true);
    const order = isOrderDescending ? "updatedAt desc" : "updatedAt asc";
    const [filters, setFilters] = useState<unknown[]>([]);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [sharedStatusSwitcher, setSharedStatusSwitcher] = useState<
      string | null
    >(null);
    const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
    const [projectsToDelete, setProjectsToDelete] = useState<string[]>([]);
    const [isSelectAll, setIsSelectAll] = useState(false);

    const {
      selected: selectedDepartment,
      setSelected: setSelectedDepartment,
      options: departmentOptions,
    } = useUserDepartments();

    const projectListQueryParams = useMemo(
      () => ({
        limit: itemsPerPage || 10,
        page: currentPageNo || 1,
        order,
        filters: {
          searchQuery,
          statuses: filterStatuses,
          sharedStatusSwitcher,
          departmentId: selectedDepartment?.value,
        },
      }),
      [
        currentPageNo,
        filterStatuses,
        itemsPerPage,
        order,
        searchQuery,
        selectedDepartment?.value,
        sharedStatusSwitcher,
      ],
    );

    // Get project list and detect changes that invoke refetching the project list
    const {
      isLoading,
      error,
      refetch: refetchProjectList,
      data: {
        filteredCount,
        statusColors,
        projects,
        pagesCount,
        projectStatuses,
        statistics,
        overallStatistics,
        projectsCount,
      },
    } = useProjectListQuery({ variables: projectListQueryParams });

    // resets projectFilters when overallStatistics updates
    const resetCallback = useCallback(() => setFilters([]), []);

    const projectConfiguratorObj = page.get("redirectToProjectCreate") || page;
    const projectItemObj = page.get("redirectToProjectItem") || page;

    const resetUrlParams = useCallback(() => {
      const projectRefs = home?.get("configLeftSidebarProjectRefs") as
        | Scrivito.Obj[]
        | null
        | undefined;
      const projectList = projectRefs?.[2];
      if (projectList) {
        Scrivito.navigateTo(projectList);
      }
    }, [home]);

    const projectListContextValue = useMemo<ProjectListContextValue>(
      () => ({
        projectsToDelete,
        setProjectsToDelete,
        projects,
        refetchProjectList,
        currentPageNo,
        setCurrentPageNo,
        resetUrlParams,
        projectId,
        projectsCount,
        itemsPerPage,
        resetCallback,
        searchQuery,
        setSearchQuery,
        overallStatistics,
        setSharedStatusSwitcher,
        projectStatuses,
        setFilterStatuses,
        setFilters,
        filters,
        statistics,
        handleItemsPerPageChange,
        error: error ?? null,
        isLoading,
        isOrderDescending,
        projectItemObj,
        projectConfiguratorObj,
        setIsOrderDescending,
        statusColors,
        filteredCount,
        pagesCount,
        setIsItemsPerPageChanged,
        selectedDepartment,
        setSelectedDepartment: setSelectedDepartment as (
          option: DepartmentOption | null,
        ) => void,
        departmentOptions: departmentOptions as DepartmentOption[],
        handleSetCurrentPage,
        isSelectAll,
        setIsSelectAll,
        projectListQueryParams,
      }),
      [
        projectsToDelete,
        setProjectsToDelete,
        projects,
        refetchProjectList,
        currentPageNo,
        setCurrentPageNo,
        resetUrlParams,
        projectId,
        projectsCount,
        itemsPerPage,
        resetCallback,
        searchQuery,
        setSearchQuery,
        overallStatistics,
        setSharedStatusSwitcher,
        projectStatuses,
        setFilterStatuses,
        setFilters,
        filters,
        statistics,
        handleItemsPerPageChange,
        error,
        isLoading,
        isOrderDescending,
        projectItemObj,
        projectConfiguratorObj,
        setIsOrderDescending,
        statusColors,
        filteredCount,
        pagesCount,
        setIsItemsPerPageChanged,
        selectedDepartment,
        setSelectedDepartment,
        departmentOptions,
        handleSetCurrentPage,
        isSelectAll,
        setIsSelectAll,
        projectListQueryParams,
      ],
    );

    return (
      <ProjectListContext.Provider value={projectListContextValue}>
        {children}
      </ProjectListContext.Provider>
    );
  },
);
